import { useMemo, useState } from "react";
import { Button, Chip, Modal, type Column } from "@legal-500/legal500-design-system";
import { aiParser, DATA_CLASSIFICATION_LABELS, type AiSchema } from "@/api/aiParser.service";
import { DataTable } from "@/components/DataTable/DataTable";
import EmptyState from "@/components/EmptyState";
import LoadingState from "@/components/LoadingState";
import { PageHeader } from "@/components/PageHeader";
import { Toggle } from "@/components/Toggle/Toggle";
import { UnauthorizedView } from "@/components/UnauthorizedView";
import { useQuery } from "@/hooks/useApi";
import { usePermissions } from "@/hooks/usePermissions";
import { SchemaFormModal } from "./SchemaFormModal";
import { TestSchemaModal } from "./TestSchemaModal";
import styles from "./AiSchemas.module.css";

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function AiSchemasPage() {
  const { hasPermission } = usePermissions();
  const canConfigure = hasPermission("submission.config");

  const { data, loading, error, refetch } = useQuery(() => aiParser.listSchemas(), []);
  const schemas = useMemo(() => data?.schemas ?? [], [data]);
  const enabledCount = schemas.filter(schema => schema.enabled).length;

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<AiSchema | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [deleting, setDeleting] = useState<AiSchema | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deletePending, setDeletePending] = useState(false);

  const [testing, setTesting] = useState<AiSchema | null>(null);

  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  function openCreate() {
    setEditing(null);
    setSaveError(null);
    setFormOpen(true);
  }

  function openEdit(schema: AiSchema) {
    setEditing(schema);
    setSaveError(null);
    setFormOpen(true);
  }

  async function handleSubmit(input: Parameters<typeof aiParser.createSchema>[0]) {
    setSaving(true);
    setSaveError(null);
    try {
      if (editing) {
        await aiParser.updateSchema(editing.id, input);
      } else {
        await aiParser.createSchema(input);
      }
      setFormOpen(false);
      setEditing(null);
      await refetch();
    } catch (caught) {
      setSaveError(errorMessage(caught));
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleEnabled(schema: AiSchema) {
    setTogglingId(schema.id);
    setRowError(null);
    try {
      await aiParser.updateSchema(schema.id, { enabled: !schema.enabled });
      await refetch();
    } catch (caught) {
      setRowError(errorMessage(caught));
    } finally {
      setTogglingId(null);
    }
  }

  async function handleDelete() {
    if (!deleting) return;
    setDeletePending(true);
    setDeleteError(null);
    try {
      await aiParser.deleteSchema(deleting.id);
      setDeleting(null);
      await refetch();
    } catch (caught) {
      setDeleteError(errorMessage(caught));
    } finally {
      setDeletePending(false);
    }
  }

  const columns: Column<AiSchema>[] = useMemo(
    () => [
      {
        header: "Name",
        accessor: "name",
        render: (_value, row) => (
          <div className={styles.nameCell}>
            <span className={styles.nameText}>{row.name}</span>
            <span className={styles.slugText}>{row.slug}</span>
          </div>
        ),
      },
      {
        header: "Provider",
        accessor: "provider",
        render: (_value, row) => (
          <div className={styles.modelCell}>
            <span>{row.provider}</span>
            <span className={styles.slugText}>{row.model}</span>
          </div>
        ),
      },
      {
        header: "Thinking",
        accessor: "thinkingLevel",
        render: (_value, row) => row.thinkingLevel ?? "unset",
      },
      {
        header: "Temperature",
        accessor: "temperature",
        render: (_value, row) => (row.temperature === null ? "—" : String(row.temperature)),
      },
      {
        header: "Classification",
        accessor: "dataClassification",
        render: (_value, row) => (
          <span className={styles.classification} data-level={row.dataClassification}>
            {DATA_CLASSIFICATION_LABELS[row.dataClassification] ?? row.dataClassification}
          </span>
        ),
      },
      {
        header: "Enabled",
        accessor: "enabled",
        render: (_value, row) => (
          <div className={styles.enabledCell}>
            <Toggle
              checked={row.enabled}
              disabled={!canConfigure || togglingId === row.id}
              onChange={() => handleToggleEnabled(row)}
              ariaLabel={`${row.enabled ? "Disable" : "Enable"} ${row.name}`}
            />
            <Chip
              size="small"
              variant="filled"
              className={row.enabled ? styles.onChip : styles.offChip}
            >
              {row.enabled ? "Running on every submission" : "Off"}
            </Chip>
          </div>
        ),
      },
      {
        header: "Actions",
        accessor: "id",
        render: (_value, row) => (
          <div className={styles.rowActions}>
            <Button size="small" variant="outlined" onClick={() => openEdit(row)}>
              Edit
            </Button>
            {/* Works on a disabled schema too — trying one before enabling it is the point. */}
            <Button size="small" variant="outlined" onClick={() => setTesting(row)}>
              Test
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={() => {
                setDeleteError(null);
                setDeleting(row);
              }}
            >
              Delete
            </Button>
          </div>
        ),
      },
    ],
    [canConfigure, togglingId],
  );

  if (!canConfigure) {
    return (
      <UnauthorizedView message="You need the submission.config permission to configure AI schemas." />
    );
  }

  return (
    <div className={styles.wrapper}>
      <div>
        <PageHeader
          title="AI Schemas"
          subtitle="JSON Schemas the AI submission parser extracts against"
        >
          <Button variant="contained" onClick={openCreate}>
            New schema
          </Button>
        </PageHeader>
      </div>

      <div className={styles.callout} role="note">
        <p className={styles.calloutTitle}>
          {enabledCount === 0
            ? "No schema is enabled — nothing is being extracted"
            : `${enabledCount} schema${enabledCount === 1 ? "" : "s"} enabled`}
        </p>
        <p className={styles.calloutBody}>
          Every enabled schema runs against every submission document. There is no template matching
          and no per-submission configuration: two enabled schemas means two paid model calls per
          document. Enabling a second schema is how you compare a new extraction against the current
          one — and it doubles the spend while it is on.
        </p>
      </div>

      {rowError && (
        <div className={styles.errorBanner} role="alert">
          {rowError}
        </div>
      )}

      {loading && <LoadingState message="Loading AI schemas..." />}

      {!loading && error && (
        <div className={styles.errorBanner} role="alert">
          Could not load AI schemas: {error.message}
        </div>
      )}

      {!loading && !error && schemas.length === 0 && (
        <EmptyState
          variant="empty"
          title="No AI schemas"
          message="Create a schema to describe what the parser should extract from a submission."
        />
      )}

      {!loading && !error && schemas.length > 0 && (
        <DataTable data={schemas} columns={columns} rowKey="id" size="small" />
      )}

      <SchemaFormModal
        isOpen={formOpen}
        schema={editing}
        saving={saving}
        saveError={saveError}
        onClose={() => setFormOpen(false)}
        onSubmit={handleSubmit}
      />

      <TestSchemaModal schema={testing} onClose={() => setTesting(null)} />

      <Modal
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        title="Delete AI schema"
        size="small"
      >
        <p className={styles.confirmText}>
          Delete <strong>{deleting?.name}</strong> and its markdown templates? A schema that has
          ever produced a parse run cannot be deleted — disable it instead.
        </p>
        {deleteError && (
          <div className={styles.errorBanner} role="alert">
            {deleteError}
          </div>
        )}
        <div className={styles.formActions}>
          <Button variant="outlined" onClick={() => setDeleting(null)} disabled={deletePending}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deletePending}>
            {deletePending ? "Deleting…" : "Delete"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}

export default AiSchemasPage;
